// Tema claro/escuro. Escuro por padrão (mesmo do Desktop e do BI); a escolha fica no navegador.
//
// O localStorage aqui é só conveniência de quem está vendo — nunca sessão nem dado. Ler ou gravar
// pode lançar (janela anônima, armazenamento bloqueado), por isso o try/catch: sem ele, um
// navegador restrito quebraria a página inteira por causa de uma preferência de cor.
import { useCallback, useSyncExternalStore } from 'react';

export type Tema = 'dark' | 'light';
const CHAVE = 'plugchef-admin:tema';

function ler(): Tema {
  try {
    return window.localStorage.getItem(CHAVE) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/** Aplica no <html> — os tokens de cor (index.css) mudam por `data-theme`, sem recompilar. */
export function aplicarTemaGuardado(): void {
  document.documentElement.dataset.theme = ler();
}

const assinantes = new Set<() => void>();

export function useTema(): [Tema, () => void] {
  const tema = useSyncExternalStore(
    (fn) => {
      assinantes.add(fn);
      return () => {
        assinantes.delete(fn);
      };
    },
    () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark'),
    () => 'dark' as Tema,
  );

  const alternar = useCallback(() => {
    const novo: Tema = document.documentElement.dataset.theme === 'light' ? 'dark' : 'light';
    document.documentElement.dataset.theme = novo;
    try {
      window.localStorage.setItem(CHAVE, novo);
    } catch {
      /* preferência não guardada: vale só nesta aba */
    }
    assinantes.forEach((fn) => fn());
  }, []);

  return [tema, alternar];
}

/** Lembrança por navegador (ex.: o último cliente escolhido). Falha em silêncio, como o tema. */
export function lembrar(chave: string, valor: string | null): void {
  try {
    if (valor === null) window.localStorage.removeItem(`plugchef-admin:${chave}`);
    else window.localStorage.setItem(`plugchef-admin:${chave}`, valor);
  } catch {
    /* sem armazenamento: só não lembra */
  }
}

export function lembrado(chave: string): string | null {
  try {
    return window.localStorage.getItem(`plugchef-admin:${chave}`);
  } catch {
    return null;
  }
}
